import React, { useState, useRef, useEffect } from "react";
import { cn } from "@/lib/utils";
import { ChevronDown, ChevronUp } from "lucide-react";
import { Button } from "@/components/ui/button";

export interface Message {
  role: "system" | "user" | "assistant" | "event";
  content: string;
  timestamp: number;
  annotation?: string;
}

interface MessageHistoryProps {
  messages: Message[];
  className?: string;
  title?: string;
  roleLabels?: {
    system?: string;
    user?: string;
    assistant?: string;
    event?: string;
  };
  variant?: "bubble" | "full";
  position?: "left" | "right";
}

export const MessageHistory: React.FC<MessageHistoryProps> = ({
  messages,
  className,
  title,
  roleLabels = {
    system: "System",
    user: "User",
    assistant: "Assistant",
  },
  variant = "full",
  position = "right",
}) => {
  const [isExpanded, setIsExpanded] = useState(false);
  const [bubbleWidth, setBubbleWidth] = useState(0);
  const buttonRef = useRef<HTMLButtonElement>(null);
  const contentRef = useRef<HTMLDivElement>(null);
  const [isAtBottom, setIsAtBottom] = useState(true);

  useEffect(() => {
    if (buttonRef.current) {
      const width = buttonRef.current.scrollWidth;
      setBubbleWidth(width);
    }
  }, [title, messages.length]);

  // Handle scroll position
  useEffect(() => {
    const content = contentRef.current;
    if (!content) return;

    const handleScroll = () => {
      const { scrollTop, scrollHeight, clientHeight } = content;
      const isBottom = Math.abs(scrollHeight - clientHeight - scrollTop) < 10;
      setIsAtBottom(isBottom);
    };

    content.addEventListener("scroll", handleScroll);
    return () => content.removeEventListener("scroll", handleScroll);
  }, []);

  // Auto-scroll when new messages arrive
  useEffect(() => {
    const content = contentRef.current;
    if (!content || !isAtBottom) return;

    content.scrollTop = content.scrollHeight;
  }, [messages, isAtBottom]);

  const content = (
    <div
      ref={contentRef}
      className={cn(
        "flex flex-col gap-2 p-4 overflow-y-auto flex-1",
        className
      )}
    >
      {messages.map((message, index) => (
        <div
          key={index}
          className={cn(
            "flex",
            message.role === "assistant"
              ? "justify-end"
              : message.role === "event"
              ? "justify-center"
              : "justify-start"
          )}
        >
          {message.role === "event" ? (
            <div className="text-sm text-muted-foreground italic py-4">
              {message.content}
            </div>
          ) : (
            <div
              className={cn(
                "flex flex-col gap-2 max-w-[80%]",
                message.role === "assistant" ? "items-end" : "items-start"
              )}
            >
              <div
                className={cn(
                  "rounded-lg text-sm px-3 py-2",
                  message.role === "assistant"
                    ? "bg-primary text-primary-foreground"
                    : "bg-muted text-muted-foreground"
                )}
              >
                <div className="font-medium mb-1">
                  {roleLabels[message.role]}
                </div>
                <div className="whitespace-pre-wrap">{message.content}</div>
              </div>
              {message.annotation && (
                <div className="mt-1 text-xs text-muted-foreground italic">
                  {message.annotation}
                </div>
              )}
            </div>
          )}
        </div>
      ))}
    </div>
  );

  const getPreviewText = () => {
    if (messages.length === 0) return "No messages";
    const lastMessage = messages[messages.length - 1];
    const content = lastMessage.content;
    return content.length > 30 ? content.slice(0, 30) + "..." : content;
  };

  if (variant === "bubble") {
    return (
      <div
        className={cn(
          "fixed bottom-4 z-50 transition-all duration-200",
          position === "right" ? "right-4" : "left-4",
          isExpanded ? "w-[400px] h-[500px]" : `w-[${bubbleWidth}px] h-8`
        )}
      >
        <div className="flex flex-col h-full bg-background border rounded-lg shadow-lg">
          <Button
            ref={buttonRef}
            variant="ghost"
            size="sm"
            className="flex items-center justify-between px-2 py-1 h-8 border-b whitespace-nowrap rounded-t-lg"
            onClick={() => setIsExpanded(!isExpanded)}
          >
            <div className="flex items-center gap-2">
              <span className="text-sm font-medium">
                {title} ({messages.length})
              </span>
            </div>
            {isExpanded ? (
              <ChevronDown className="w-4 h-4 flex-shrink-0" />
            ) : (
              <ChevronUp className="w-4 h-4 flex-shrink-0" />
            )}
          </Button>
          {isExpanded && content}
        </div>
      </div>
    );
  }

  return (
    <div className="flex flex-col h-full border rounded-lg">
      {title && (
        <div className="p-2 border-b text-sm font-medium">
          {title} ({messages.length})
        </div>
      )}
      {content}
    </div>
  );
};
